// src/pages/agents/EditAgent.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Alert,
  Badge,
  Toast,
  ToastContainer
} from "react-bootstrap";
import axios from "axios";
import {
  Pencil,
  Person,
  Envelope,
  Phone,
  Key,
  Shield,
  Shop,
  Check,
  X,
  ArrowLeft,
  Save,
  InfoCircle,
  ExclamationCircle,
  FileText
} from "react-bootstrap-icons";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { API_BASE_URL } from "../constants/api_constants";

const USERS_ENDPOINT = `${API_BASE_URL}/users`;
const MARCHES_ENDPOINT = `${API_BASE_URL}/marches`;

const agentTypes = [
  { value: "hors_marche", label: "Agent hors marché", description: "Agent sans marché assigné", color: "secondary" },
  { value: "marche", label: "Agent de marché", description: "Agent assigné à un marché spécifique", color: "primary" },
  { value: "admin_principal", label: "Admin Principal", description: "Administrateur des marchés (ODD/Commune)", color: "danger" }
];

export default function EditAgent() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingMarches, setLoadingMarches] = useState(false);
  const [marches, setMarches] = useState([]);
  const [agent, setAgent] = useState(null);
  
  // ✅ Utiliser un toast pour les notifications
  const [toast, setToast] = useState({
    show: false,
    message: '',
    type: 'info'
  });
  
  const [formData, setFormData] = useState({
    admin_lname: '',
    admin_fname: '',
    admin_phone: '',
    admin_fonction: '',
    email: '',
    matricule_interne: '',
    code_agent_interne: '',
    agent_type: 'hors_marche',
    id_marche: null
  });

  const getAuthToken = () => sessionStorage.getItem("authToken");
  const getHeaders = () => {
    const token = getAuthToken();
    const headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  };

  // ✅ Afficher une notification toast
  const showToast = (message, type = 'info') => {
    setToast({ show: true, message, type });
    // Auto-hide après 5 secondes
    setTimeout(() => {
      setToast(prev => ({ ...prev, show: false }));
    }, 5000);
  };

  const fetchMarches = async () => {
    try {
      setLoadingMarches(true);
      const response = await axios.get(MARCHES_ENDPOINT, { 
        headers: getHeaders(),
        params: { per_page: 100 }
      });
      
      let marchesData = [];
      if (response.data && response.data.code === 200) {
        marchesData = response.data.data || [];
      } else if (response.data && Array.isArray(response.data)) {
        marchesData = response.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        marchesData = response.data.data;
      }
      
      setMarches(marchesData);
    } catch (error) {
      console.error("Erreur chargement marchés:", error);
      setMarches([]);
    } finally {
      setLoadingMarches(false);
    }
  };

  const fetchAgent = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${USERS_ENDPOINT}/${id}`, { headers: getHeaders() });
      
      if (response.data && response.data.user) {
        const userData = response.data.user;
        
        console.log("Données utilisateur:", userData);
        
        let agentType = 'hors_marche';
        if (userData.role_id === 1003 || userData.role_id === 1001) {
          agentType = 'admin_principal';
        } else if (userData.id_marche) {
          agentType = 'marche';
        }
        
        setFormData({
          admin_lname: userData.admin_lname || userData.full_name || userData.admin_lname || '',
          admin_fname: userData.admin_fname || userData.first_name || userData.admin_fname || '',
          admin_phone: userData.admin_phone || userData.telephone || '',
          admin_fonction: userData.admin_fonction || userData.fonction || '',
          email: userData.email || '',
          matricule_interne: userData.matricule_interne || '',
          code_agent_interne: userData.code_agent_interne || '',
          agent_type: agentType,
          id_marche: userData.id_marche || null
        });
        setAgent(userData);
      } else {
        showToast("Données utilisateur non trouvées", 'warning');
      }
    } catch (error) {
      console.error("Erreur fetchAgent:", error);
      showToast(error.response?.data?.message || 'Erreur lors du chargement', 'danger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMarches();
    fetchAgent();
  }, [id]);

  const getRoleIdFromType = (agentType) => {
    switch (agentType) {
      case 'admin_principal':
        return 1003;
      case 'marche':
        return 1005;
      case 'hors_marche':
      default:
        return 1013;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.admin_lname.trim() || !formData.admin_fname.trim() || 
        !formData.admin_phone.trim() || !formData.admin_fonction.trim()) {
      showToast("Veuillez remplir tous les champs obligatoires", 'warning');
      return;
    }

    if (formData.agent_type === 'marche' && !formData.id_marche) {
      showToast("Veuillez sélectionner un marché pour l'agent", 'warning');
      return;
    }

    setSaving(true);
    
    const roleId = getRoleIdFromType(formData.agent_type);
    const updateData = {
      full_name: formData.admin_lname.trim(),
      first_name: formData.admin_fname.trim(),
      fonction: formData.admin_fonction.trim(),
      telephone: formData.admin_phone.trim(),
      email: formData.email.trim(),
      matricule_interne: formData.matricule_interne.trim(),
      code_agent_interne: formData.code_agent_interne.trim(),
      role_id: roleId,
      id_marche: formData.agent_type === 'marche' ? formData.id_marche : null
    };
    
    try {
      await axios.put(`${USERS_ENDPOINT}/${id}`, updateData, { headers: getHeaders() });
      showToast('Agent mis à jour avec succès ', 'success');
      setTimeout(() => navigate("/superAdmin/Agents"), 1500);
    } catch (error) {
      showToast(error.response?.data?.message || 'Erreur lors de la mise à jour ', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAgentTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      agent_type: type,
      id_marche: type === 'marche' ? prev.id_marche : null
    }));
  };

  const handlePhoneChange = (e) => {
    const onlyNumbers = e.target.value.replace(/\D/g, '');
    setFormData(prev => ({ ...prev, admin_phone: onlyNumbers }));
  };

  if (loading) {
    return (
      <React.Fragment>
        <Header />
        <div className="main main-app p-4">
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" style={{ width: '48px', height: '48px' }} />
            <h5 className="mt-3 text-muted">Chargement de l'agent...</h5>
          </div>
        </div>
        <Footer />
      </React.Fragment>
    );
  }

  const selectedType = agentTypes.find(t => t.value === formData.agent_type);

  return (
    <React.Fragment>
      <Header />
      
      {/* ✅ ToastContainer FIXE - visible partout même en bas de page */}
      <ToastContainer 
        className="p-3" 
        style={{ 
          zIndex: 99999, 
          position: 'fixed', 
          top: '80px', 
          right: '20px',
          maxWidth: '450px',
          width: '100%'
        }}
      >
        <Toast 
          show={toast.show} 
          onClose={() => setToast(prev => ({ ...prev, show: false }))} 
          delay={5000} 
          autohide
          style={{
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
            borderRadius: '12px',
            border: 'none'
          }}
        >
          <Toast.Header 
            className={`bg-${toast.type === 'success' ? 'success' : 
                        toast.type === 'danger' ? 'danger' : 
                        toast.type === 'warning' ? 'warning' : 'info'} text-white`}
            style={{ borderRadius: '12px 12px 0 0' }}
          >
            <strong className="me-auto">
              {toast.type === 'success' && ' Succès'}
              {toast.type === 'danger' && ' Erreur'}
              {toast.type === 'warning' && ' Attention'}
              {toast.type === 'info' && ' Information'}
            </strong>
            <small className="text-white-50">Maintenant</small>
          </Toast.Header>
          <Toast.Body className="text-dark py-3" style={{ fontSize: '14px' }}>
            {toast.message}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="main main-app p-3 p-lg-4" style={{ background: '#f5f7fa', minHeight: '100vh' }}>
        <div className="container-fluid">
          
          {/* En-tête */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <ol className="breadcrumb bg-transparent p-0 mb-1">
                <li className="breadcrumb-item">
                  <Link to="/superAdmin/Agents" className="text-decoration-none">Agents</Link>
                </li>
                <li className="breadcrumb-item active text-primary">Modifier l'agent</li>
              </ol>
              <h4 className="main-title mb-0 fw-bold">Modifier l'agent</h4>
              <p className="text-muted mb-0 small">
                {agent?.admin_fname || agent?.first_name || ''} {agent?.admin_lname || agent?.full_name || ''}
              </p>
            </div>
            <Link to="/superAdmin/Agents" className="btn btn-outline-secondary rounded-pill">
              <ArrowLeft className="me-2" size={16} /> Retour
            </Link>
          </div>

          {/* Formulaire */}
          <Card className="border-0 shadow-sm rounded-3">
            <Card.Header className="bg-white border-bottom py-3">
              <div className="d-flex align-items-center justify-content-between">
                <h6 className="mb-0 fw-semibold">
                  <Pencil className="me-2 text-primary" size={20} />
                  Informations de l'agent
                </h6>
                <Badge bg={selectedType?.color || 'secondary'} className="px-3 py-2">
                  {selectedType?.label}
                </Badge>
              </div>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                {/* Type d'agent */}
                <div className="mb-4">
                  <h6 className="text-muted mb-3 border-bottom pb-2">
                    <Shield className="me-2" size={16} /> Type d'agent
                  </h6>
                  <Row className="g-3">
                    {agentTypes.map((type) => (
                      <Col key={type.value} lg={4} md={6} sm={12}>
                        <div 
                          className={`p-3 border rounded-3 h-100 w-100 ${formData.agent_type === type.value ? 'border-2' : ''}`}
                          style={{
                            backgroundColor: formData.agent_type === type.value ? 'rgba(13, 110, 253, 0.05)' : 'transparent',
                            borderColor: formData.agent_type === type.value ? '#0d6efd' : '#dee2e6',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onClick={() => handleAgentTypeChange(type.value)}
                        >
                          <Form.Check
                            type="radio"
                            id={`edit-agent-type-${type.value}`}
                            label={
                              <div>
                                <span className="fw-medium">{type.label}</span>
                                <br />
                                <small className="text-muted">{type.description}</small>
                              </div>
                            }
                            value={type.value}
                            checked={formData.agent_type === type.value}
                            onChange={() => handleAgentTypeChange(type.value)}
                            className="w-100"
                            style={{ cursor: 'pointer' }}
                          />
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>

                {/* Sélection du marché */}
                {formData.agent_type === 'marche' && (
                  <div className="mb-4">
                    <h6 className="text-muted mb-3 border-bottom pb-2">
                      <Shop className="me-2" size={16} /> Sélection du marché
                    </h6>
                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>Marché <span className="text-danger">*</span></Form.Label>
                          <Form.Select
                            value={formData.id_marche || ''}
                            onChange={(e) => setFormData({...formData, id_marche: e.target.value || null})}
                            required
                          >
                            <option value="">Sélectionner un marché...</option>
                            {marches.map((marche) => (
                              <option key={marche.id_marche} value={marche.id_marche}>
                                {marche.nom_marche} {marche.code_marche ? `(${marche.id_marche})` : ''}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>
                )}

                {/* Informations personnelles */}
                <div className="mb-4">
                  <h6 className="text-muted mb-3 border-bottom pb-2">
                    <Person className="me-2" size={16} /> Informations personnelles
                  </h6>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Nom <span className="text-danger">*</span></Form.Label>
                        <Form.Control 
                          type="text" 
                          name="admin_lname"
                          value={formData.admin_lname} 
                          onChange={handleChange}
                          required 
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Prénom <span className="text-danger">*</span></Form.Label>
                        <Form.Control 
                          type="text" 
                          name="admin_fname"
                          value={formData.admin_fname} 
                          onChange={handleChange}
                          required 
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Téléphone <span className="text-danger">*</span></Form.Label>
                        <Form.Control 
                          type="tel" 
                          name="admin_phone"
                          value={formData.admin_phone} 
                          onChange={handlePhoneChange}
                          required 
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                        <Form.Control 
                          type="email" 
                          name="email"
                          value={formData.email} 
                          onChange={handleChange}
                          required 
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Fonction</Form.Label>
                        <Form.Control 
                          type="text" 
                          name="admin_fonction"
                          value={formData.admin_fonction} 
                          onChange={handleChange}
                          placeholder="Fonction de l'agent"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* Identifiants Internes */}
                <div className="mb-4">
                  <h6 className="text-muted mb-3 border-bottom pb-2">
                    <Key className="me-2" size={16} /> Identifiants Internes
                  </h6>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Matricule Interne</Form.Label>
                        <Form.Control 
                          type="text" 
                          name="matricule_interne"
                          value={formData.matricule_interne} 
                          onChange={handleChange}
                          placeholder="Ex: MAT-2024-001"
                        />
                        <Form.Text className="text-muted">
                          Identifiant unique interne de l'agent
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Code Agent Interne</Form.Label>
                        <Form.Control 
                          type="text" 
                          name="code_agent_interne"
                          value={formData.code_agent_interne} 
                          onChange={handleChange}
                          placeholder="Ex: AG-2024-001"
                        />
                        <Form.Text className="text-muted">
                          Code unique pour identification interne
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* Boutons */}
                <hr className="my-4" />
                <div className="d-flex gap-3 justify-content-end">
                  <Link to="/superAdmin/Agents" className="btn btn-outline-secondary rounded-pill">
                    <X className="me-2" size={16} /> Annuler
                  </Link>
                  <Button 
                    type="submit" 
                    variant="primary" 
                    className="rounded-pill"
                    disabled={
                      saving ||
                      !formData.admin_lname.trim() ||
                      !formData.admin_fname.trim() ||
                      !formData.admin_phone.trim() ||
                      !formData.email.trim() ||
                      (formData.agent_type === 'marche' && !formData.id_marche)
                    }
                  >
                    {saving ? (
                      <><Spinner animation="border" size="sm" className="me-2" />Mise à jour...</>
                    ) : (
                      <><Save className="me-2" size={16} /> Mettre à jour</>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </div>
        <Footer />
      </div>
    </React.Fragment>
  );
}